import API from '@aws-amplify/api';
import { openAIWebRTC } from '@/graphql/mutations';
import Utils from '@/common/utils/utils';
import TelephoneConstant from '@/common/constant/TelephoneConstant';

export const handleAction = async (requestDto) => {
    try {
        let res = await API.graphql({
            query: openAIWebRTC,
            variables: {
                openAIWebRTCRequestDto: requestDto
            },
        });
        let resultData = res.data.openAIWebRTC;
        resultData = {...resultData, data: Utils.convertJSONDataToObject(resultData.data)};
        return resultData;
    } catch (err) {
        console.log('[openAIWebRTCService] handleAction error >>>', err);
        return null;
    }
};

export const createSession = async (corpId, projectId, userId, options = {}) => {
    const requestDto = {
        corporationInuse: corpId,
        projectInuse: projectId,
        userId: userId,
        actionType: TelephoneConstant.ACTION.OPENAI_WEBRTC_CREATE_SESSION,
        requestBody: JSON.stringify({
            aiOperatorTicketId: options.aiOperatorTicketId,
        }),
    };

    const resultData = await handleAction(requestDto);
    return resultData;
};

export const callConnected = async (corpId, projectId, userId, { ticketId, aiOperatorTicketId, connectedAt }) => {
    const requestDto = {
        corporationInuse: corpId,
        projectInuse: projectId,
        userId: userId,
        actionType: TelephoneConstant.ACTION.OPENAI_WEBRTC_CALL_CONNECTED,
        requestBody: JSON.stringify({ ticketId, aiOperatorTicketId, connectedAt }),
    };

    const resultData = await handleAction(requestDto);
    return resultData;
};

export const saveTranscript = async (corpId, projectId, userId, { role, text, chatThreadId, aiOperatorTicketId, ticketId }) => {
    const requestDto = {
        corporationInuse: corpId,
        projectInuse: projectId,
        userId: userId,
        actionType: TelephoneConstant.ACTION.OPENAI_WEBRTC_SAVE_TRANSCRIPT,
        requestBody: JSON.stringify({ role, text, chatThreadId, aiOperatorTicketId, ticketId }),
    };

    const resultData = await handleAction(requestDto);
    return resultData;
};

export const saveRecording = async (corpId, projectId, userId, { filename, s3Key, ticketId }) => {
    const requestDto = {
        corporationInuse: corpId,
        projectInuse: projectId,
        userId: userId,
        actionType: TelephoneConstant.ACTION.OPENAI_WEBRTC_SAVE_RECORDING,
        requestBody: JSON.stringify({ filename, s3Key, ticketId }),
    };

    const resultData = await handleAction(requestDto);
    return resultData;
};

export const endCall = async (
    corpId,
    projectId,
    userId,
    { ticketId, chatThreadId, duration, aiTrainingChatThreadId, aiOperatorTicketId, logSif }
) => {
    const requestDto = {
        corporationInuse: corpId,
        projectInuse: projectId,
        userId: userId,
        actionType: TelephoneConstant.ACTION.OPENAI_WEBRTC_END_CALL,
        requestBody: JSON.stringify({
            ticketId,
            chatThreadId,
            duration,
            aiTrainingChatThreadId,
            aiOperatorTicketId,
            logSif
        })
    };

    const resultData = await handleAction(requestDto);
    return resultData;
};

export const updateHeartbeat = async (corpId, projectId, userId, { ticketId }) => {
    const requestDto = {
        corporationInuse: corpId,
        projectInuse: projectId,
        userId: userId,
        actionType: TelephoneConstant.ACTION.OPENAI_WEBRTC_UPDATE_HEARTBEAT,
        requestBody: JSON.stringify({ ticketId }),
    };

    const resultData = await handleAction(requestDto);
    return resultData;
};

export const incrementHoldCount = async (corpId, projectId, userId, { ticketId }) => {
    const requestDto = {
        corporationInuse: corpId,
        projectInuse: projectId,
        userId: userId,
        actionType: TelephoneConstant.ACTION.OPENAI_WEBRTC_INCREMENT_HOLD_COUNT,
        requestBody: JSON.stringify({ ticketId }),
    };

    const resultData = await handleAction(requestDto);
    return resultData;
};

/**
 * Execute an AI tools call (SIF) requested by the OpenAI Realtime API during a
 * training call. Relays the function name + parsed arguments to the WebRTC_Handler,
 * which runs the SIF (knn_search / knowledge_read / ticket_read) and returns the
 * result (CR-2026-005 / TEL-009 R4).
 *
 * @param {string} corpId
 * @param {string} projectId
 * @param {string} userId
 * @param {Object} options
 * @param {string} options.functionName - SIF name from the function_call event
 * @param {Object} options.arguments - parsed arguments from the function_call event
 * @param {string} options.aiOperatorTicketId - prompt ticket (permission scope)
 * @param {string} [options.ticketId] - training telephone ticket
 * @returns {Promise<Object|null>} parsed backend data { functionName, statusCode, result }
 */
export const executeFunctionCall = async (
    corpId,
    projectId,
    userId,
    { functionName, arguments: functionArgs, aiOperatorTicketId, ticketId }
) => {
    const requestDto = {
        corporationInuse: corpId,
        projectInuse: projectId,
        userId: userId,
        actionType: TelephoneConstant.ACTION.OPENAI_WEBRTC_EXECUTE_FUNCTION_CALL,
        requestBody: JSON.stringify({
            functionName,
            arguments: functionArgs,
            aiOperatorTicketId,
            ticketId,
        }),
    };

    const resultData = await handleAction(requestDto);
    return resultData;
};

